import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './ProfileValidationOverlay.css';

const ProfileValidationOverlay = ({ isOpen, onClose, onComplete, missingFields, userProfile, listing }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    moveInDate: '',
    occupation: '',
    monthlyIncome: '',
    pets: 'no',
    smoking: 'no',
    creditScore: '',
    numAdults: '1',
    numKids: '0',
    termDuration: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [consentToShare, setConsentToShare] = useState(false);
  // Removed unused state variables that were causing ESLint warnings

  // Move creditScoreOptions before useEffect to avoid 'use before define' warning
  const creditScoreOptions = useMemo(() => [
    { value: '800', label: '750+' },
    { value: '725', label: '700-749' },
    { value: '675', label: '650-699' },
    { value: '600', label: 'below 650' }
  ], []);

  // Initialize form data with existing profile data
  useEffect(() => {
    if (userProfile) {
      setFormData(prev => ({
        ...prev,
        firstName: userProfile.firstName || '',
        lastName: userProfile.lastName || '',
        email: userProfile.email || '',
        phoneNumber: userProfile.phoneNumber || '',
        moveInDate: userProfile.moveInDate || '',
        occupation: userProfile.occupation || '',
        monthlyIncome: userProfile.monthlyIncome || '',
        pets: userProfile.pets || 'no',
        smoking: userProfile.smoking || 'no',
        creditScore: (() => {
          const creditScoreValue = userProfile.creditScore || '';
          // If it's already a value (like '675'), keep it
          if (creditScoreOptions.find(opt => opt.value === creditScoreValue)) {
            return creditScoreValue;
          }
          // If it's a label (like '650-699'), find the corresponding value
          const optionByLabel = creditScoreOptions.find(opt => opt.label === creditScoreValue);
          return optionByLabel ? optionByLabel.value : creditScoreValue;
        })(),
        numAdults: userProfile.numAdults || '1',
        numKids: userProfile.numKids || '0',
        termDuration: userProfile.termDuration || ''
      }));
    }
  }, [userProfile, creditScoreOptions]);

  // creditScoreOptions moved above to avoid 'use before define' warning

  const termDurationOptions = [
    { value: '1-3 months', label: '1-3 months' },
    { value: '3-6 months', label: '3-6 months' },
    { value: '6-12 months', label: '6-12 months' },
    { value: '12+ months', label: '12+ months' }
  ];

  const petsOptions = [
    { value: 'no', label: 'No Pets' },
    { value: 'yes', label: 'Have Pets' }
  ];

  const smokingOptions = [
    { value: 'no', label: 'Non-Smoker' },
    { value: 'yes', label: 'Smoker' }
  ];

  // Removed unused handleInputChange function to fix ESLint warning

  const formatMoveInDate = (dateString) => {
    if (!dateString) return '';
    try {
      // Parse as local date to avoid timezone issues
      const [year, month, day] = dateString.split('-');
      const date = new Date(year, month - 1, day); // month is 0-indexed
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  const formatIncome = (income) => {
    if (!income) return '';
    return parseFloat(income).toLocaleString();
  };

  // Helper function to add proper article (a/an) before occupation
  const addArticle = (occupation) => {
    if (!occupation) return '';
    const vowels = ['a', 'e', 'i', 'o', 'u'];
    const firstLetter = occupation.toLowerCase().charAt(0);
    const article = vowels.includes(firstLetter) ? 'an' : 'a';
    return `${article} ${occupation}`;
  };

  // Helper function to pluralize adults/kids
  const pluralize = (count, singular, plural = null) => {
    const num = parseInt(count) || 0;
    if (num === 1) return `${num} ${singular}`;
    const pluralForm = plural || `${singular}s`;
    return `${num} ${pluralForm}`;
  };

  // Generate the message based on current form data
  const generateMessage = useCallback(() => {
    const agentName = userProfile.agentName ? 
      userProfile.agentName.charAt(0).toUpperCase() + userProfile.agentName.slice(1).toLowerCase() : 
      'Listing Agent';
    
    const address = listing?.address || 'this property';
    const firstName = formData.firstName || userProfile.firstName || '[First Name]';
    const lastName = formData.lastName || userProfile.lastName || '[Last Name]';
    const occupation = formData.occupation || userProfile.occupation || '[Occupation]';
    const income = formatIncome(formData.monthlyIncome || userProfile.monthlyIncome) || '[monthly income]';
    const creditScoreValue = formData.creditScore || userProfile.creditScore || '';
    // Map credit score to proper range label
    const creditScore = creditScoreValue ? 
      (creditScoreOptions.find(opt => opt.value === creditScoreValue)?.label || 
       creditScoreOptions.find(opt => opt.label === creditScoreValue)?.label || 
       creditScoreValue) : 
      '[select...]';
    const numAdults = formData.numAdults || userProfile.numAdults || '1';
    const numKidsValue = formData.numKids || userProfile.numKids || 'no';
    const numKids = (numKidsValue === '0' || numKidsValue === 0) ? 'no' : numKidsValue;
    const moveInDate = formatMoveInDate(formData.moveInDate || userProfile.moveInDate) || '[Move-in Date]';
    const termDuration = formData.termDuration || userProfile.termDuration || '[select...]';
    const phoneNumber = formData.phoneNumber || userProfile.phoneNumber || '[Phone Number]';

    const adultsText = pluralize(numAdults, 'adult');
    const kidsText = numKids === 'no' ? 'no kids' : pluralize(numKids, 'kid');
    const pets = formData.pets || userProfile.pets || 'no';
    const smoking = formData.smoking || userProfile.smoking || 'no';
    const occupationWithArticle = occupation !== '[Occupation]' ? addArticle(occupation.toLowerCase()) : '[Occupation]';
    
    // Generate pets and smoking text according to specifications
    let petsSmokingText = '';
    const petsValue = String(pets).toLowerCase();
    const smokingValue = String(smoking).toLowerCase();
    
    if (petsValue === 'no' && smokingValue === 'no') {
      petsSmokingText = 'Please note that I have no pets and a non-smoker.';
    } else if (petsValue === 'no' && smokingValue === 'yes') {
      petsSmokingText = 'Please note that I have no pet and only smoke outside the property.';
    } else if (petsValue === 'yes' && smokingValue === 'no') {
      petsSmokingText = 'Please note that I have pets and a non-smoker.';
    } else if (petsValue === 'yes' && smokingValue === 'yes') {
      petsSmokingText = 'Please note that I have pets and only smoke outside the property.';
    } else {
      // Fallback with default values
      petsSmokingText = 'Please note that I have no pets and a non-smoker.';
    }
    
    return `Hi ${agentName}, I'm interested in renting ${address}. My name is ${firstName} ${lastName}, ${occupationWithArticle} with monthly income of $${income} and credit score of ${creditScore}. The household will be ${adultsText} with ${kidsText}. ${petsSmokingText} Preferred move-in date is ${moveInDate}, planning to stay ${termDuration}. Please contact me at ${phoneNumber}.`;
  }, [formData, userProfile, listing, creditScoreOptions]);

  // Note: generateMessage is called directly in handleSubmit when needed

  // Handle keyboard events
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
      // Prevent Enter from submitting form when editing fields
      if (e.key === 'Enter' && editingField) {
        e.preventDefault();
        setEditingField(null);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }
  }, [isOpen, editingField, onClose]);

  const renderEditableField = (fieldName, displayValue) => {
    const isMissing = isMissingField(fieldName);
    const isEditing = editingField === fieldName;

    const handleInputChange = (value) => {
      setFormData(prev => ({ ...prev, [fieldName]: value }));
    };

    if (isEditing) {
      // Render input based on field type
      switch (fieldName) {
        case 'moveInDate':
          return (
            <input
              type="date"
              value={formData[fieldName] || ''}
              onChange={(e) => handleInputChange(e.target.value)}
              onBlur={() => setEditingField(null)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setEditingField(null);
                }
              }}
              autoFocus
              style={{border: 'none', borderBottom: '1px solid #333', background: 'transparent', fontSize: '18px'}}
            />
          );
        case 'creditScore':
          return (
            <select
              value={formData[fieldName] || ''}
              onChange={(e) => { handleInputChange(e.target.value); setEditingField(null); }}
              onBlur={() => setEditingField(null)}
              autoFocus
              style={{border: 'none', borderBottom: '1px solid #333', background: 'transparent', fontSize: '18px'}}
            >
              <option value="">Select...</option>
              {creditScoreOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          );
        case 'termDuration':
          return (
            <select
              value={formData[fieldName] || ''}
              onChange={(e) => { handleInputChange(e.target.value); setEditingField(null); }}
              onBlur={() => setEditingField(null)}
              autoFocus
              style={{border: 'none', borderBottom: '1px solid #333', background: 'transparent', fontSize: '18px'}}
            >
              <option value="">Select...</option>
              {termDurationOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          );
        case 'pets':
          return (
            <select
              value={formData[fieldName] || ''}
              onChange={(e) => { handleInputChange(e.target.value); setEditingField(null); }}
              onBlur={() => setEditingField(null)}
              autoFocus
              style={{border: 'none', borderBottom: '1px solid #333', background: 'transparent', fontSize: '18px'}}
            >
              <option value="">Select...</option>
              {petsOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          );
        case 'smoking':
          return (
            <select
              value={formData[fieldName] || ''}
              onChange={(e) => { handleInputChange(e.target.value); setEditingField(null); }}
              onBlur={() => setEditingField(null)}
              autoFocus
              style={{border: 'none', borderBottom: '1px solid #333', background: 'transparent', fontSize: '18px'}}
            >
              <option value="">Select...</option>
              {smokingOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          );
        case 'numAdults':
          return (
            <select
              value={formData[fieldName] || ''}
              onChange={(e) => { handleInputChange(e.target.value); setEditingField(null); }}
              onBlur={() => setEditingField(null)}
              autoFocus
              style={{border: 'none', borderBottom: '1px solid #333', background: 'transparent', fontSize: '18px'}}
            >
              {[...Array(11)].map((_, i) => (
                <option key={i} value={i.toString()}>{i}</option>
              ))}
            </select>
          );
        case 'numKids':
          return (
            <select
              value={formData[fieldName] || ''}
              onChange={(e) => { handleInputChange(e.target.value); setEditingField(null); }}
              onBlur={() => setEditingField(null)}
              autoFocus
              style={{border: 'none', borderBottom: '1px solid #333', background: 'transparent', fontSize: '18px'}}
            >
              <option value="no">no</option>
              {[...Array(10)].map((_, i) => (
                <option key={i+1} value={(i+1).toString()}>{i+1}</option>
              ))}
            </select>
          );
        case 'monthlyIncome':
          return (
            <input
              type="number"
              value={formData[fieldName] || ''}
              onChange={(e) => handleInputChange(e.target.value)}
              onBlur={() => setEditingField(null)}
              autoFocus
              style={{border: 'none', borderBottom: '1px solid #333', background: 'transparent', fontSize: '18px'}}
            />
          );
        default:
          return (
            <input
              type="text"
              value={formData[fieldName] || ''}
              onChange={(e) => handleInputChange(e.target.value)}
              onBlur={() => setEditingField(null)}
              autoFocus
              style={{border: 'none', borderBottom: '1px solid #333', background: 'transparent', fontSize: '18px'}}
            />
          );
      }
    }

    return (
      <span 
        style={{
          color: isMissing ? '#333' : '#198754',
          backgroundColor: isMissing ? '#fff3cd' : 'transparent',
          padding: isMissing ? '2px 6px' : '0',
          borderRadius: isMissing ? '4px' : '0',
          cursor: 'pointer', 
          textDecoration: 'underline'
        }} 
        onClick={() => setEditingField(fieldName)}
      >
        {displayValue}
      </span>
    );
  };

  // Inline token helpers
  const isMissingField = (field) => {
    const val = formData[field];
    return val === undefined || val === null || `${val}`.trim() === '';
  };

  // Removed unused renderToken function and tokenClass to fix ESLint warnings

  const validateForm = () => {
    const newErrors = {};
    
    // Define required fields
    const requiredFields = ['firstName', 'lastName', 'occupation', 'monthlyIncome', 'creditScore', 'numAdults', 'numKids', 'pets', 'smoking', 'moveInDate', 'termDuration', 'phoneNumber'];

    requiredFields.forEach(field => {
      if (!formData[field] || formData[field].toString().trim() === '') {
        newErrors[field] = 'This field is required';
      }
    });

    // Additional validation for specific fields
    if (formData.email && formData.email.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    if (formData.monthlyIncome) {
      const income = parseFloat(formData.monthlyIncome);
      if (isNaN(income) || income <= 0) {
        newErrors.monthlyIncome = 'Please enter a valid income amount';
      }
    }

    if (formData.moveInDate) {
      const moveInDate = new Date(formData.moveInDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to avoid time comparison issues
      if (moveInDate < today) {
        newErrors.moveInDate = 'Move-in date cannot be in the past';
      }
    }

    // Validate consent checkbox
    if (!consentToShare) {
      newErrors.consent = 'You must consent to share your information to send the application';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Generate the final message from the fill-in-the-blanks and send with profile data
      const finalMessage = generateMessage();
      onComplete({
        profileData: formData,
        message: finalMessage,
        consentToShare: consentToShare
      });
    } catch (error) {
      console.error('Error completing profile:', error);
      alert('Error saving profile information. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // (Old inline input renderer removed and replaced with tokenized approach)

  if (!isOpen) return null;

  return (
    <div className="profile-validation-overlay">
      <div className="profile-validation-modal fill-in-blanks">
        <div className="modal-header">
          <h2>Complete Your Rental Application</h2>
          <p>Please fill in the missing information in the message below. This is what will be sent to the listing agent:</p>
        </div>

        <form onSubmit={handleSubmit} className="message-form">
          <div className="message-preview">
            <p style={{fontSize: '18px', lineHeight: '1.6', margin: '20px 0', color: '#333'}}>
              Hi {userProfile.agentName ? userProfile.agentName.charAt(0).toUpperCase() + userProfile.agentName.slice(1).toLowerCase() : 'Farshad'}, I'm interested in renting {listing?.address || 'this property'}. My name is {renderEditableField('firstName', formData.firstName || userProfile.firstName || 'First')} {renderEditableField('lastName', formData.lastName || userProfile.lastName || 'Last')}, {(() => {
                const occupation = formData.occupation || userProfile.occupation || 'occupation';
                const occupationWithArticle = occupation !== 'occupation' ? addArticle(occupation.toLowerCase()) : 'occupation';
                return renderEditableField('occupation', occupationWithArticle);
              })()} with monthly income of ${renderEditableField('monthlyIncome', formatIncome(formData.monthlyIncome || userProfile.monthlyIncome) || 'monthly income')} and credit score of {(() => {
                const creditScoreValue = formData.creditScore || userProfile.creditScore || '';
                const displayValue = creditScoreValue ? 
                  (creditScoreOptions.find(opt => opt.value === creditScoreValue)?.label || 
                   creditScoreOptions.find(opt => opt.label === creditScoreValue)?.label || 
                   creditScoreValue) : 
                  'select...';
                return renderEditableField('creditScore', displayValue);
              })()}. The household will be {(() => {
                const numAdults = formData.numAdults || userProfile.numAdults || '1';
                const adultsText = pluralize(numAdults, 'adult');
                return renderEditableField('numAdults', adultsText);
              })()} with {(() => {
                const kidsValue = formData.numKids || userProfile.numKids || 'no';
                const displayValue = (kidsValue === '0' || kidsValue === 0) ? 'no' : kidsValue;
                if (displayValue === 'no') {
                  return <>{renderEditableField('numKids', 'no kids')}</>;
                } else {
                  const kidsText = pluralize(displayValue, 'kid');
                  return <>{renderEditableField('numKids', kidsText)}</>;
                }
              })()} {(() => {
                const petsValue = formData.pets || userProfile.pets || 'no';
                const smokingValue = formData.smoking || userProfile.smoking || 'no';
                
                if (petsValue === 'no' && smokingValue === 'no') {
                  return <>Please note that I have {renderEditableField('pets', 'no pets')} and {renderEditableField('smoking', 'a non-smoker')}.</>;
                } else if (petsValue === 'no' && smokingValue === 'yes') {
                  return <>Please note that I have {renderEditableField('pets', 'no pet')} and {renderEditableField('smoking', 'only smoke outside the property')}.</>;
                } else if (petsValue === 'yes' && smokingValue === 'no') {
                  return <>Please note that I have {renderEditableField('pets', 'pets')} and {renderEditableField('smoking', 'a non-smoker')}.</>;
                } else if (petsValue === 'yes' && smokingValue === 'yes') {
                  return <>Please note that I have {renderEditableField('pets', 'pets')} and {renderEditableField('smoking', 'only smoke outside the property')}.</>;
                } else {
                  return <>Please note that I {renderEditableField('pets', petsValue === 'no' ? 'have no pets' : 'have pets')} and {renderEditableField('smoking', smokingValue === 'no' ? 'am a non-smoker' : 'only smoke outside the property')}.</>;
                }
              })()} Preferred move-in date is {renderEditableField('moveInDate', formatMoveInDate(formData.moveInDate || userProfile.moveInDate) || 'yyyy-mm-dd')}, planning to stay {renderEditableField('termDuration', formData.termDuration || userProfile.termDuration || 'select...')}. Please contact me at {renderEditableField('phoneNumber', formData.phoneNumber || userProfile.phoneNumber || '(647) 123-4567')}.
            </p>
          </div>

          {Object.keys(errors).length > 0 && (
            <div className="form-errors">
              <p>Please complete the highlighted fields above to continue.</p>
            </div>
          )}

          <div className="consent-section">
            <label className="consent-checkbox">
              <input
                type="checkbox"
                checked={consentToShare}
                onChange={(e) => setConsentToShare(e.target.checked)}
                className={errors.consent ? 'error' : ''}
              />
              <span className="checkmark"></span>
              <span className="consent-text">
                By clicking "Send Application", I consent to the terms and conditions and agree to share my credit report and application information with <strong>{listing?.agent?.name || 'the listing agent'}</strong>.
              </span>
            </label>
            {errors.consent && (
              <div className="field-error">
                {errors.consent}
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="cancel-button"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Application'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileValidationOverlay;